/**
 * Client-side cookie preferences helper.
 *
 * Until the user logs in, preferences are stored in localStorage under
 * `careos:cookie-prefs`. Once authenticated, preferences sync to
 * `public.user_cookie_preferences` (handled by CookieBanner on first accept
 * post-login).
 */

export type CookiePrefs = {
  analytics: boolean;
  marketing: boolean;
  preferences: boolean; // always true (essential + functional)
  decidedAt: string | null; // ISO timestamp, null = user has not decided yet
};

const STORAGE_KEY = "careos:cookie-prefs";

const DEFAULT_PREFS: CookiePrefs = {
  analytics: false,
  marketing: false,
  preferences: true,
  decidedAt: null,
};

export function getCookiePrefs(): CookiePrefs {
  if (typeof window === "undefined") return DEFAULT_PREFS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<CookiePrefs>;
    return {
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      preferences: parsed.preferences !== false,
      decidedAt: parsed.decidedAt ?? null,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function setCookiePrefs(next: Omit<CookiePrefs, "decidedAt">): CookiePrefs {
  const full: CookiePrefs = {
    ...next,
    decidedAt: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(full));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent("careos:cookie-prefs-changed", { detail: full }));
  return full;
}

export function hasDecidedCookies(): boolean {
  return getCookiePrefs().decidedAt !== null;
}

export function analyticsAllowed(): boolean {
  return getCookiePrefs().analytics;
}

export function marketingAllowed(): boolean {
  return getCookiePrefs().marketing;
}
